import { useState, useRef, useMemo, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeft, Printer, ChevronDown, ChevronUp, Clock, PackageCheck, TrendingUp, Lock, AlertCircle, X, FileText, Check, Store, CalendarHeart, Globe, Truck } from 'lucide-react'
import { RezzAiIcon } from '@/components/ai/RezzAiIcon'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PageSpinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/utils'
import {
  useMenuItem,
  useMenuCategories,
  useSuppliers,
  useSaveMenuItem,
  useIngredients,
  useSaveIngredient,
  useAddons,
  useMenuItemAddons,
  useSaveMenuItemAddons,
  useDeleteMenuItem,
  useImageUpload,
} from '@/hooks/useMenu'
import { useCateringPricing, useSaveCateringPricing } from '@/hooks/useCatering'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryClient'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type {
  MenuItem,
  MenuItemStatus,
  SourceType,
  AiRecipe,
  AiIngredient,
  AiPrepStep,
  AiPostProdStep,
  AiStep,
  MenuItemCosting,
  Ingredient,
  Supplier,
  CateringItemPricing,
} from '@/types/app'

// A stable fallback for query results that haven't loaded yet. Using a fresh
// `[]` literal as a destructuring default creates a new array reference on
// every render, which — if that value is a useEffect dependency — causes the
// effect to re-fire every render, which can spiral into "Maximum update depth
// exceeded". This one reference never changes, so it's safe to depend on.
const EMPTY_ARRAY: never[] = []

type LibIngredient = Ingredient & {
  ingredient_suppliers: Array<{
    id: string; ingredient_id: string; supplier_id: string
    is_preferred: boolean; unit_price: number | null; notes: string | null
    supplier: Supplier | null
  }>
}

const PRINT_LABELS = {
  en: {
    prep: 'PREP — DO AHEAD',
    ingredients: 'INGREDIENTS',
    procedure: 'PROCEDURE',
    postProd: 'POST-PRODUCTION & PACKING',
    notes: "CHEF'S NOTES",
    qty: 'Qty', unit: 'Unit', ingredient: 'Ingredient', brand: 'Brand', prepNote: 'Prep note',
    yield: 'Yield', time: 'Time',
    step: 'Step',
  },
  es: {
    prep: 'PREPARACIÓN — CON ANTICIPACIÓN',
    ingredients: 'INGREDIENTES',
    procedure: 'PROCEDIMIENTO',
    postProd: 'POST-PRODUCCIÓN Y EMPAQUE',
    notes: 'NOTAS DEL CHEF',
    qty: 'Cant.', unit: 'Unidad', ingredient: 'Ingrediente', brand: 'Marca', prepNote: 'Nota',
    yield: 'Rendimiento', time: 'Tiempo',
    step: 'Paso',
  },
} as const
type PrintLang = keyof typeof PRINT_LABELS

// Unit conversion table — factor to multiply to convert FROM key TO inner key
const UNIT_CONV: Record<string, Record<string, number>> = {
  g:          { g: 1, kg: 0.001, oz: 0.035274, lb: 0.0022046 },
  kg:         { kg: 1, g: 1000, oz: 35.274, lb: 2.20462 },
  oz:         { oz: 1, lb: 0.0625, g: 28.3495, kg: 0.0283495 },
  lb:         { lb: 1, oz: 16, g: 453.592, kg: 0.453592 },
  ml:         { ml: 1, l: 0.001, fl_oz: 0.033814 },
  l:          { l: 1, ml: 1000, fl_oz: 33.814, gal: 0.264172, qt: 1.05669 },
  fl_oz:      { fl_oz: 1, ml: 29.5735, l: 0.0295735 },
  gal:        { gal: 1, l: 3.78541, ml: 3785.41, qt: 4, fl_oz: 128 },
  qt:         { qt: 1, l: 0.946353, ml: 946.353, gal: 0.25 },
  tablespoon: { tablespoon: 1, tbsp: 1, g: 15, ml: 15, tsp: 3 },
  tbsp:       { tbsp: 1, tablespoon: 1, g: 15, ml: 15, tsp: 3 },
  tsp:        { tsp: 1, g: 5, ml: 5 },
  cup:        { cup: 1, ml: 240, l: 0.24, g: 240, fl_oz: 8 },
}

const UNIT_ALIASES: Record<string, string> = {
  kilo: 'kg', kilos: 'kg', kilogram: 'kg', kilograms: 'kg',
  gram: 'g', grams: 'g',
  liter: 'l', litre: 'l', liters: 'l', litres: 'l',
  milliliter: 'ml', millilitre: 'ml', milliliters: 'ml', millilitres: 'ml',
  ounce: 'oz', ounces: 'oz',
  pound: 'lb', pounds: 'lb',
  gallon: 'gal', gallons: 'gal',
  quart: 'qt', quarts: 'qt',
  tablespoons: 'tablespoon', tbsps: 'tbsp',
  teaspoon: 'tsp', teaspoons: 'tsp',
  cups: 'cup',
  pieces: 'piece', pcs: 'piece',
}

function normUnit(u: string): string {
  const s = u.toLowerCase().trim()
  return UNIT_ALIASES[s] ?? s
}

function convertQty(qty: number, from: string, to: string): number | null {
  const f = normUnit(from)
  const t = normUnit(to)
  if (f === t) return qty
  const row = UNIT_CONV[f]
  if (!row) return null
  const factor = row[t]
  return factor !== undefined ? qty * factor : null
}

const CONVERTER_UNITS = ['g', 'kg', 'oz', 'lb', 'ml', 'l', 'fl_oz', 'gal', 'qt', 'tablespoon', 'tsp', 'cup']

function UnitConverterWidget() {
  const [open, setOpen] = useState(false)
  const [val, setVal] = useState('1')
  const [from, setFrom] = useState('tablespoon')
  const [to, setTo] = useState('g')

  const parsed = parseFloat(val)
  const result = !isNaN(parsed) ? convertQty(parsed, from, to) : null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 border border-stone-200 rounded px-2.5 py-1.5 bg-white transition-colors"
      >
        ⇄ Unit Converter
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 bg-white border border-stone-200 rounded-lg shadow-modal p-3 w-72">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">Quick Converter</span>
            <button type="button" onClick={() => setOpen(false)} className="ml-auto text-stone-300 hover:text-stone-600"><X size={13} /></button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={val}
              onChange={e => setVal(e.target.value)}
              className="w-20 h-8 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <select value={from} onChange={e => setFrom(e.target.value)} className="flex-1 h-8 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
              {CONVERTER_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <span className="text-stone-400 shrink-0">→</span>
            <select value={to} onChange={e => setTo(e.target.value)} className="flex-1 h-8 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
              {CONVERTER_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="mt-2 text-sm">
            {result !== null
              ? <span className="font-semibold text-stone-900">{result.toFixed(result < 0.1 ? 4 : result < 1 ? 3 : 2)} {to}</span>
              : <span className="text-stone-400 italic">Can&apos;t convert between these units</span>
            }
          </div>
        </div>
      )}
    </div>
  )
}

function IngredientCombobox({
  value,
  library,
  onChange,
}: {
  value: string
  library: LibIngredient[]
  onChange: (name: string, libEntry?: LibIngredient) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(value)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setQuery(value) }, [value])

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = library
    .filter(lib => lib.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 8)

  return (
    <div className="relative" ref={containerRef}>
      <input
        value={query}
        onChange={e => {
          setQuery(e.target.value)
          onChange(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search library..."
        className="w-full h-7 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 z-50 mt-0.5 w-64 bg-white border border-stone-200 rounded shadow-modal max-h-48 overflow-y-auto">
          {filtered.map(lib => (
            <button
              key={lib.id}
              type="button"
              onMouseDown={e => {
                e.preventDefault()
                setQuery(lib.name)
                onChange(lib.name, lib)
                setOpen(false)
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-stone-50 text-left gap-2"
            >
              <span className="font-medium text-stone-900 truncate">{lib.name}</span>
              {lib.brand && (
                <span className="text-xs text-brand-700 bg-brand-100 px-1.5 py-0.5 rounded-full shrink-0">{lib.brand}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function QuickAddIngredientPanel({
  initialName,
  restaurantId,
  onClose,
  onAdded,
}: {
  initialName: string
  restaurantId: string
  onClose: () => void
  onAdded: () => void
}) {
  const saveIngredient = useSaveIngredient()
  const qc = useQueryClient()
  const [name, setName] = useState(initialName)
  const [brand, setBrand] = useState('')
  const [unit, setUnit] = useState('g')
  const [cost, setCost] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    if (!name.trim()) { setError('Name is required'); return }
    setError(null)
    try {
      await saveIngredient.mutateAsync({
        name: name.trim(),
        brand: brand.trim() || null,
        unit,
        cost_per_unit: cost ? parseFloat(cost) : null,
        is_perishable: false,
        item_type: 'ingredient',
        material_category: null,
      })
      qc.invalidateQueries({ queryKey: queryKeys.ingredients(restaurantId) })
      onAdded()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  const UNITS = ['g', 'kg', 'ml', 'L', 'lb', 'oz', 'piece', 'dozen', 'case']

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <h2 className="text-base font-semibold text-stone-900">Add to Ingredient Library</h2>
            <p className="text-xs text-stone-400 mt-0.5">This will be available in all recipes</p>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <p className="text-sm text-danger">{error}</p>}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">Name <span className="text-danger">*</span></label>
            <input value={name} onChange={e => setName(e.target.value)} className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">Brand / Manufacturer</label>
            <input value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. Real, Tata, Local" className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="flex gap-3">
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-sm font-medium text-stone-700">Unit</label>
              <select value={unit} onChange={e => setUnit(e.target.value)} className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1.5 flex-1">
              <label className="text-sm font-medium text-stone-700">Cost / unit</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">$</span>
                <input type="number" min="0" step="0.001" value={cost} onChange={e => setCost(e.target.value)} placeholder="0.00" className="flex h-9 w-full rounded border border-stone-300 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-stone-200">
          <button
            type="button"
            onClick={handleSave}
            disabled={saveIngredient.isPending}
            className="w-full h-9 rounded bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-60 transition-colors"
          >
            {saveIngredient.isPending ? 'Saving...' : 'Add to Library'}
          </button>
        </div>
      </div>
    </>
  )
}

type Tab = 'details' | 'recipe' | 'costing'

const DIETARY_FLAGS = ['veg', 'vegan', 'gf', 'dairy_free', 'nut_free', 'halal']
const ALLERGENS = ['nuts', 'gluten', 'dairy', 'eggs', 'shellfish', 'soy']

function ChipToggle({
  value,
  selected,
  onToggle,
}: {
  value: string
  selected: boolean
  onToggle: (v: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(value)}
      className={[
        'px-2.5 py-1 rounded text-xs font-medium border transition-colors',
        selected
          ? 'bg-brand-500 text-white border-brand-500'
          : 'bg-white text-stone-600 border-stone-300 hover:border-brand-400',
      ].join(' ')}
    >
      {value}
    </button>
  )
}

const MAX_IMAGES = 2

function ImageUploadSection({ item }: { item: MenuItem | null }) {
  const saveItem   = useSaveMenuItem()
  const uploadImage = useImageUpload()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [images, setImages] = useState<string[]>([])
  const [imgError, setImgError] = useState<string | null>(null)

  useEffect(() => { setImages(item?.images ?? []) }, [item?.id])

  async function handleFiles(files: FileList | null) {
    if (!files || !item?.id) return
    const file = files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setImgError('Only image files allowed'); return }
    if (images.length >= MAX_IMAGES) { setImgError(`Max ${MAX_IMAGES} photos`); return }
    setImgError(null)
    try {
      const url = await uploadImage.mutateAsync({ file, menuItemId: item.id })
      const next = [...images, url]
      setImages(next)
      await saveItem.mutateAsync({ id: item.id, images: next })
    } catch (e) {
      setImgError(e instanceof Error ? e.message : 'Upload failed')
    }
  }

  async function handleRemove(url: string) {
    if (!item?.id) return
    const next = images.filter(u => u !== url)
    setImages(next)
    await saveItem.mutateAsync({ id: item.id, images: next })
  }

  if (!item?.id) {
    return (
      <div className="space-y-1.5">
        <span className="text-sm font-medium text-stone-700">Photos</span>
        <p className="text-xs text-stone-400">Save the item first to upload photos.</p>
      </div>
    )
  }

  const uploading = uploadImage.isPending

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-stone-700">
        Photos{' '}
        <span className="text-xs font-normal text-stone-400">({images.length}/{MAX_IMAGES})</span>
      </span>

      <div className="flex items-center gap-3 flex-wrap">
        {images.map(url => (
          <div key={url} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-stone-200 bg-stone-50 shrink-0">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => handleRemove(url)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={11} />
            </button>
          </div>
        ))}

        {images.length < MAX_IMAGES && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files) }}
            onDragOver={e => e.preventDefault()}
            disabled={uploading}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 hover:border-brand-400 text-stone-400 hover:text-brand-500 flex flex-col items-center justify-center gap-0.5 transition-colors disabled:opacity-40 shrink-0 cursor-pointer"
          >
            {uploading
              ? <span className="text-xs">Uploading…</span>
              : <><span className="text-2xl leading-none font-light">+</span><span className="text-2xs">Photo</span></>
            }
          </button>
        )}
      </div>

      {imgError && <p className="text-xs text-danger">{imgError}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => { handleFiles(e.target.files); (e.target as HTMLInputElement).value = '' }}
      />
    </div>
  )
}

function DetailsTab({
  item,
  onSaved,
}: {
  item: MenuItem | null
  onSaved: (saved: MenuItem) => void
}) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { data: categories } = useMenuCategories()
  const { data: suppliers } = useSuppliers()
  const saveItem = useSaveMenuItem()
  const { mutateAsync: deleteItem, isPending: isDeleting } = useDeleteMenuItem()
  const { data: allAddons = EMPTY_ARRAY } = useAddons()
  const { data: currentAddons = EMPTY_ARRAY } = useMenuItemAddons(item?.id ?? '')
  const { mutateAsync: saveAddons } = useSaveMenuItemAddons()
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([])
  const [retireConfirm, setRetireConfirm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const defaultCategoryId = searchParams.get('category') ?? ''

  const role = useAuthStore(s => s.user?.role)
  const canSeeWholesale = role === 'owner' || role === 'ops'

  const { data: pricingMap }                     = useCateringPricing()
  const { mutateAsync: saveCateringPricing }      = useSaveCateringPricing()

  const blankCatering = (): Partial<CateringItemPricing> => ({
    half_tray_price: null, half_tray_serves: null, half_tray_pieces: null,
    medium_tray_price: null, medium_tray_serves: null, medium_tray_pieces: null,
    full_tray_price: null, full_tray_serves: null, full_tray_pieces: null,
    per_piece_price: null, min_pieces: null,
  })

  const [catering, setCatering] = useState<Partial<CateringItemPricing>>(blankCatering)

  useEffect(() => {
    if (item?.id && pricingMap) {
      const p = pricingMap.get(item.id)
      setCatering(p ?? blankCatering())
    }
  }, [item?.id, pricingMap])

  // Auto-select first category for new items when categories load
  useEffect(() => {
    if (!form.is_addon && !form.category_id && categories && categories.length > 0) {
      const first = [...categories].sort((a, b) => a.display_order - b.display_order)[0]
      setForm(f => ({ ...f, category_id: first.id }))
    }
  }, [categories])

  useEffect(() => {
    setSelectedAddonIds(currentAddons.map(a => a.addon_id))
  }, [currentAddons])

  async function toggleAddon(addonId: string) {
    const newIds = selectedAddonIds.includes(addonId)
      ? selectedAddonIds.filter(id => id !== addonId)
      : [...selectedAddonIds, addonId]
    setSelectedAddonIds(newIds)
    // New (unsaved) product — nothing to persist to yet. Selections are held
    // locally and written once the product is saved and has a real id.
    if (!item?.id) return
    await saveAddons({ menuItemId: item.id, addonIds: newIds })
  }

  function cp(field: keyof CateringItemPricing, value: string, isInt = false) {
    const num = value === '' ? null : isInt ? parseInt(value) : parseFloat(value)
    setCatering(p => ({ ...p, [field]: isNaN(num as number) ? null : num }))
  }

  function cv(field: keyof CateringItemPricing): string {
    const v = catering[field]
    return v == null ? '' : String(v)
  }

  const isAddonParam = searchParams.get('addon') === 'true'

  const [form, setForm] = useState({
    name: item?.name ?? '',
    description: item?.description ?? '',
    price: item?.price?.toString() ?? '0',
    wholesale_price: item?.wholesale_price?.toString() ?? '',
    upc_code: item?.upc_code ?? '',
    regional_name: item?.regional_name ?? '',
    category_id: item?.category_id ?? defaultCategoryId,
    status: (item?.status ?? 'active') as MenuItemStatus,
    source_type: (item?.source_type ?? 'inhouse') as SourceType,
    procured_supplier_id: item?.procured_supplier_id ?? '',
    available_in_store: item?.available_in_store ?? true,
    available_catering: item?.available_catering ?? false,
    available_store_supply: item?.available_store_supply ?? false,
    available_online: item?.available_online ?? false,
    dietary_flags: item?.dietary_flags ?? [],
    allergens: item?.allergens ?? [],
    is_addon: item?.is_addon ?? isAddonParam,
  })
  const [error, setError] = useState<string | null>(null)

  function toggleChip(arr: string[], val: string): string[] {
    return arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]
  }

  async function handleRetire() {
    if (!item?.id) return
    setError(null)
    try {
      await saveItem.mutateAsync({ id: item.id, status: 'retired' })
      setRetireConfirm(false)
      navigate('/products')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mark as retired')
    }
  }

  async function handleDelete() {
    if (!item?.id) return
    setError(null)
    try {
      await deleteItem(item.id)
      setDeleteConfirm(false)
      navigate('/products')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete this product')
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Name is required')
      return
    }
    if (!form.is_addon && !form.category_id) {
      setError('Please select a category')
      return
    }
    setError(null)
    try {
      const saved = await saveItem.mutateAsync({
        ...(item?.id ? { id: item.id } : {}),
        name: form.name,
        description: form.description || null,
        price: parseFloat(form.price) || 0,
        wholesale_price: form.wholesale_price ? parseFloat(form.wholesale_price) : null,
        upc_code: form.upc_code.trim() || null,
        regional_name: form.regional_name.trim() || null,
        category_id: form.category_id || null,
        status: form.status,
        source_type: form.source_type,
        procured_supplier_id: form.procured_supplier_id || null,
        available_in_store: form.available_in_store,
        available_catering: form.available_catering,
        available_store_supply: form.available_store_supply,
        available_online: form.available_online,
        dietary_flags: form.dietary_flags,
        allergens: form.allergens,
        is_addon: form.is_addon,
      })
      if (form.available_catering) {
        await saveCateringPricing({ ...catering, menu_item_id: saved.id })
      }
      // New products can't persist "Served With" selections until they have a
      // real id — write them now that saving produced one.
      if (!item?.id && selectedAddonIds.length > 0) {
        await saveAddons({ menuItemId: saved.id, addonIds: selectedAddonIds })
      }
      onSaved(saved)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {error && <p className="text-sm text-danger">{error}</p>}

      {/* Accompaniment badge — shown at top when item is an add-on */}
      {form.is_addon && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
            Accompaniment
          </span>
          <span className="text-xs text-stone-400">served alongside items, not sold separately</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Name"
          required
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-stone-700">Counter / Store Price</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            prefix="$"
            value={form.price}
            onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
          />
        </div>
      </div>

      {canSeeWholesale && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">
              Wholesale Price
              <span className="ml-2 text-xs font-normal text-stone-400">owner &amp; ops only</span>
            </label>
            <Input
              type="text"
              inputMode="decimal"
              prefix="$"
              placeholder="0.00"
              value={form.wholesale_price}
              onChange={e => setForm(f => ({ ...f, wholesale_price: e.target.value }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">UPC Code</label>
            <input
              value={form.upc_code}
              onChange={e => setForm(f => ({ ...f, upc_code: e.target.value }))}
              placeholder="e.g. 860010396001"
              className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm font-mono text-stone-900 placeholder:text-stone-400 placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-stone-700">
          Regional Name <span className="text-xs font-normal text-stone-400">(Telugu / secondary language)</span>
        </label>
        <input
          value={form.regional_name}
          onChange={e => setForm(f => ({ ...f, regional_name: e.target.value }))}
          placeholder="e.g. Ribbon Kaarappusa"
          className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-stone-700">Description</label>
        <textarea
          rows={3}
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="flex w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {!form.is_addon && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-stone-700">Category <span className="text-danger">*</span></label>
            <select
              value={form.category_id ?? ''}
              onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
              className={[
                'flex h-9 w-full rounded border bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
                !form.category_id ? 'border-red-300' : 'border-stone-300',
              ].join(' ')}
            >
              <option value="">Select category…</option>
              {(categories ?? []).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-stone-700">Status</label>
          <select
            value={form.status}
            onChange={e => setForm(f => ({ ...f, status: e.target.value as MenuItemStatus }))}
            className="flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="active">Active</option>
            <option value="seasonal">Seasonal</option>
            <option value="eighty_sixed">86&apos;d</option>
            <option value="retired">Retired</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-stone-700">Source Type</label>
        <div className="flex gap-2">
          {(['inhouse', 'procured'] as SourceType[]).map(st => (
            <button
              key={st}
              type="button"
              onClick={() => setForm(f => ({ ...f, source_type: st }))}
              className={[
                'px-4 py-2 rounded border text-sm font-medium transition-colors',
                form.source_type === st
                  ? 'bg-brand-500 text-white border-brand-500'
                  : 'bg-white text-stone-700 border-stone-300 hover:border-brand-400',
              ].join(' ')}
            >
              {st === 'inhouse' ? 'Inhouse' : 'Procured'}
            </button>
          ))}
        </div>
        {form.source_type === 'procured' && (
          <div className="mt-2">
            <label className="text-sm font-medium text-stone-700">Supplier</label>
            <select
              value={form.procured_supplier_id}
              onChange={e => setForm(f => ({ ...f, procured_supplier_id: e.target.value }))}
              className="mt-1.5 flex h-9 w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">Select supplier</option>
              {(suppliers ?? []).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Sales channels — each independent, same product can sell through
          any combination. Adding a future channel (e.g. a POS integration
          like Toast) is just one more card in this same pattern, not a
          redesign. */}
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-stone-700">Sales Channels</label>
        <p className="text-xs text-stone-400">Where this item can be sold — turn on any combination.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { key: 'available_in_store' as const,     icon: Store,        label: 'In-Store',  desc: 'Sold at your counter' },
            { key: 'available_online' as const,       icon: Globe,        label: 'Online Store', desc: 'Public web store' },
            { key: 'available_catering' as const,     icon: CalendarHeart, label: 'Catering',  desc: 'Catering orders & quotes' },
            { key: 'available_store_supply' as const, icon: Truck,        label: 'Wholesale', desc: 'Buyer/grocery supply' },
          ].map(({ key, icon: Icon, label, desc }) => {
            const active = form[key]
            return (
              <button
                key={key}
                type="button"
                onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                className={cn(
                  'flex flex-col items-start gap-1.5 p-3 rounded-lg border text-left transition-colors',
                  active ? 'border-brand-400 bg-brand-50' : 'border-stone-200 bg-white hover:border-stone-300',
                )}
              >
                <div className="flex items-center justify-between w-full">
                  <Icon size={16} className={active ? 'text-brand-600' : 'text-stone-400'} />
                  {active && <Check size={13} className="text-brand-600" />}
                </div>
                <span className={cn('text-sm font-medium', active ? 'text-brand-800' : 'text-stone-700')}>{label}</span>
                <span className="text-2xs text-stone-400 leading-tight">{desc}</span>
              </button>
            )
          })}
        </div>
      </div>

      {form.available_catering && (
        <div className="flex flex-col gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <label className="text-sm font-semibold text-amber-800">Catering Pricing &amp; Serving Info</label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-stone-500 border-b border-amber-200">
                  <th className="pb-2 pr-3 font-medium">Tray / Unit</th>
                  <th className="pb-2 pr-3 font-medium">Price ($)</th>
                  <th className="pb-2 pr-3 font-medium">Serves (people)</th>
                  <th className="pb-2 font-medium">Approx. Pieces</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {([
                  { label: 'Half Tray',   price: 'half_tray_price',   serves: 'half_tray_serves',   pieces: 'half_tray_pieces' },
                  { label: 'Medium Tray', price: 'medium_tray_price', serves: 'medium_tray_serves', pieces: 'medium_tray_pieces' },
                  { label: 'Full Tray',   price: 'full_tray_price',   serves: 'full_tray_serves',   pieces: 'full_tray_pieces' },
                ] as { label: string; price: keyof CateringItemPricing; serves: keyof CateringItemPricing; pieces: keyof CateringItemPricing }[]).map(row => (
                  <tr key={row.label}>
                    <td className="py-2 pr-3 font-medium text-stone-700">{row.label}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="number" min="0" step="0.01" placeholder="0.00"
                        value={cv(row.price)}
                        onChange={e => cp(row.price, e.target.value)}
                        className="w-24 h-8 rounded border border-stone-300 bg-white px-2 text-sm text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="number" min="0" step="1" placeholder="0"
                        value={cv(row.serves)}
                        onChange={e => cp(row.serves, e.target.value, true)}
                        className="w-20 h-8 rounded border border-stone-300 bg-white px-2 text-sm text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="number" min="0" step="1" placeholder="0"
                        value={cv(row.pieces)}
                        onChange={e => cp(row.pieces, e.target.value, true)}
                        className="w-20 h-8 rounded border border-stone-300 bg-white px-2 text-sm text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                      />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="py-2 pr-3 font-medium text-stone-700">Per Piece</td>
                  <td className="py-2 pr-3">
                    <input
                      type="number" min="0" step="0.01" placeholder="0.00"
                      value={cv('per_piece_price')}
                      onChange={e => cp('per_piece_price', e.target.value)}
                      className="w-24 h-8 rounded border border-stone-300 bg-white px-2 text-sm text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                    />
                  </td>
                  <td className="py-2 pr-3 text-stone-400 text-xs italic">—</td>
                  <td className="py-2">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number" min="0" step="1" placeholder="0"
                        value={cv('min_pieces')}
                        onChange={e => cp('min_pieces', e.target.value, true)}
                        className="w-20 h-8 rounded border border-stone-300 bg-white px-2 text-sm text-stone-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
                      />
                      <span className="text-xs text-stone-400">min</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-stone-700">Dietary Flags</label>
        <div className="flex flex-wrap gap-2">
          {DIETARY_FLAGS.map(flag => (
            <ChipToggle
              key={flag}
              value={flag}
              selected={form.dietary_flags.includes(flag)}
              onToggle={val => setForm(f => ({ ...f, dietary_flags: toggleChip(f.dietary_flags, val) }))}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-stone-700">Allergens</label>
        <div className="flex flex-wrap gap-2">
          {ALLERGENS.map(a => (
            <ChipToggle
              key={a}
              value={a}
              selected={form.allergens.includes(a)}
              onToggle={val => setForm(f => ({ ...f, allergens: toggleChip(f.allergens, val) }))}
            />
          ))}
        </div>
      </div>

      {/* Served With — accompaniments; hidden for addon items (they can't have their own accompaniments) */}
      {!form.is_addon && allAddons.length > 0 && (
        <div className="space-y-2">
          <div>
            <label className="text-sm font-medium text-stone-700">Served With</label>
            <p className="text-xs text-stone-400 mt-0.5">
              {item?.id
                ? 'Select accompaniments served alongside this item. Changes save instantly.'
                : 'Select accompaniments served alongside this item. Saved when you save this product.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {allAddons.map(addon => {
              const selected = selectedAddonIds.includes(addon.id)
              return (
                <button
                  key={addon.id}
                  type="button"
                  onClick={() => toggleAddon(addon.id)}
                  className={[
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    selected
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white text-stone-600 border-stone-300 hover:border-amber-400 hover:text-amber-700',
                  ].join(' ')}
                >
                  {addon.name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <ImageUploadSection item={item} />

      {/* Accompaniment toggle — near save so it feels like a classification action */}
      <label className="flex items-center gap-3 cursor-pointer select-none group pt-1">
        <div
          onClick={() => setForm(f => ({
            ...f,
            is_addon: !f.is_addon,
            category_id: !f.is_addon ? null : f.category_id,
          }))}
          className={[
            'relative w-9 h-5 rounded-full transition-colors shrink-0',
            form.is_addon ? 'bg-amber-500' : 'bg-stone-300 group-hover:bg-stone-400',
          ].join(' ')}
        >
          <span className={[
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
            form.is_addon ? 'translate-x-4' : 'translate-x-0.5',
          ].join(' ')} />
        </div>
        <div>
          <span className="text-sm font-medium text-stone-700">Mark as Accompaniment</span>
          <span className="text-xs text-stone-400 ml-2">not sold separately</span>
        </div>
      </label>

      <Button onClick={handleSave} loading={saveItem.isPending}>
        Save Details
      </Button>

      {/* Danger zone — only for saved items */}
      {item?.id && (
        <div className="pt-4 border-t border-stone-100 space-y-2">
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {/* Retire */}
            {item.status !== 'retired' && (
              retireConfirm ? (
                <span className="flex items-center gap-2 text-xs">
                  <span className="text-stone-500">Mark as retired?</span>
                  <button
                    onClick={handleRetire}
                    className="text-warning-dark font-semibold hover:underline"
                  >
                    Yes, retire it
                  </button>
                  <button
                    onClick={() => setRetireConfirm(false)}
                    className="text-stone-400 hover:text-stone-600"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => { setRetireConfirm(true); setDeleteConfirm(false) }}
                  className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
                >
                  Mark as retired
                </button>
              )
            )}

            {/* Delete — right-aligned, least prominent */}
            <div className="ml-auto">
              {deleteConfirm ? (
                <span className="flex items-center gap-2 text-xs">
                  <span className="text-stone-500">Delete permanently?</span>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="text-danger font-semibold hover:underline disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting…' : 'Yes, delete'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="text-stone-400 hover:text-stone-600"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => { setDeleteConfirm(true); setRetireConfirm(false) }}
                  className="text-xs text-stone-300 hover:text-danger transition-colors"
                >
                  Delete product
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function formatTime(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`
}

function highlightTimings(text: string): React.ReactNode[] {
  const re = /(\d+(?:[–\-]\d+)?\s*(?:min(?:utes?)?|hours?|hrs?)|overnight)/gi
  const result: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  const scanner = new RegExp(re.source, 'gi')
  while ((match = scanner.exec(text)) !== null) {
    if (match.index > lastIndex) result.push(text.slice(lastIndex, match.index))
    result.push(
      <span key={match.index} style={{
        display: 'inline-block', background: '#fef3c7', color: '#92400e',
        fontWeight: 600, fontSize: 12, padding: '1px 7px', borderRadius: 4, margin: '0 2px',
      }}>
        {match[0]}
      </span>
    )
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) result.push(text.slice(lastIndex))
  return result
}

function RecipePrintView({ item, recipe, getBrand, lang = 'en' }: {
  item: MenuItem | null
  recipe: AiRecipe
  getBrand: (name: string, id?: string) => string | null
  lang?: PrintLang
}) {
  const L = PRINT_LABELS[lang]

  const h2Style: React.CSSProperties = {
    fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, color: '#888',
    margin: '32px 0 10px', borderBottom: '1px solid #eee', paddingBottom: 6, fontWeight: 600,
  }
  const blockStyle: React.CSSProperties = {
    background: '#f9f9f9', borderLeft: '3px solid #ccc',
    padding: '10px 14px', marginBottom: 8, fontSize: 14,
  }
  const warnStyle: React.CSSProperties = {
    background: '#fff8f0', borderLeft: '3px solid #f97316',
    padding: '10px 14px', fontSize: 13, marginBottom: 8,
  }
  const blockLabelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, color: '#888', marginBottom: 2, textTransform: 'uppercase', fontWeight: 700,
  }
  const thStyle: React.CSSProperties = {
    textAlign: 'left', fontSize: 12, color: '#888', padding: '6px 8px', background: '#f5f5f5', fontWeight: 600,
  }
  const tdStyle: React.CSSProperties = {
    padding: 8, borderBottom: '1px solid #f0f0f0', fontSize: 14, verticalAlign: 'top',
  }

  return (
    <div style={{ fontFamily: 'sans-serif', color: '#222', lineHeight: 1.6 }}>

      <h1 style={{ fontSize: 24, marginBottom: 4, fontWeight: 700 }}>{item?.name ?? 'Recipe'}</h1>
      <p style={{ color: '#888', fontSize: 13, margin: '0 0 4px' }}>Kitchen SOP{lang === 'es' ? ' — Español' : ''}</p>

      <div style={{ display: 'flex', gap: 24, fontSize: 14, color: '#555', margin: '8px 0' }}>
        {recipe.serves && (
          <span>{L.yield}: <span style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{recipe.serves}</span></span>
        )}
        {recipe.total_time_minutes && (
          <span>{L.time}: <strong>{formatTime(recipe.total_time_minutes)}</strong></span>
        )}
      </div>

      {recipe.prep.length > 0 && (
        <>
          <h2 style={h2Style}>{L.prep}</h2>
          {recipe.prep.map((p, i) => (
            <div key={i} style={blockStyle}>
              <strong style={blockLabelStyle}>{p.timing}</strong>
              {p.task}
            </div>
          ))}
        </>
      )}

      <h2 style={h2Style}>{L.ingredients}</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
        <thead>
          <tr>
            <th style={thStyle}>{L.qty}</th>
            <th style={thStyle}>{L.ingredient}</th>
            <th style={thStyle}>{L.brand}</th>
            <th style={thStyle}>{L.prepNote}</th>
          </tr>
        </thead>
        <tbody>
          {recipe.ingredients.map((ing, i) => (
            <tr key={i}>
              <td style={{ ...tdStyle, fontWeight: 700, whiteSpace: 'nowrap' }}>{ing.qty} {ing.unit}</td>
              <td style={tdStyle}>{ing.name}</td>
              <td style={tdStyle}>{getBrand(ing.name, ing.ingredient_id) ?? '—'}</td>
              <td style={{ ...tdStyle, fontSize: 12, color: '#666', fontStyle: 'italic' }}>{ing.prep ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={h2Style}>{L.procedure}</h2>
      <ol style={{ paddingLeft: 20, margin: 0 }}>
        {recipe.steps.map((s) => (
          <li key={s.n} style={{ padding: '6px 0', fontSize: 14 }}>
            {highlightTimings(s.text)}
          </li>
        ))}
      </ol>

      {recipe.post_production?.length > 0 && (
        <>
          <h2 style={h2Style}>{L.postProd}</h2>
          {recipe.post_production.map((p, i) => (
            <div key={i} style={blockStyle}>
              <strong style={blockLabelStyle}>{p.timing}</strong>
              {p.task}
            </div>
          ))}
        </>
      )}

      {recipe.notes && (
        <>
          <h2 style={h2Style}>{L.notes}</h2>
          <div style={warnStyle}>{recipe.notes}</div>
        </>
      )}
    </div>
  )
}

function generateRecipeText(
  itemName: string,
  recipe: AiRecipe,
  getBrand: (name: string, id?: string) => string | null,
  lang: PrintLang
): string {
  const L = PRINT_LABELS[lang]
  const lines: string[] = []

  lines.push(itemName.toUpperCase())
  lines.push('='.repeat(itemName.length))
  const meta: string[] = []
  if (recipe.serves) meta.push(`${L.yield}: ${recipe.serves}`)
  if (recipe.total_time_minutes) meta.push(`${L.time}: ${formatTime(recipe.total_time_minutes)}`)
  if (meta.length) lines.push(meta.join('  |  '))
  lines.push('')

  if (recipe.prep.length > 0) {
    lines.push(L.prep)
    lines.push('-'.repeat(L.prep.length))
    for (const p of recipe.prep) {
      lines.push(`□  ${p.timing}: ${p.task}`)
    }
    lines.push('')
  }

  lines.push(L.ingredients)
  lines.push('-'.repeat(L.ingredients.length))
  // Column widths
  const col1 = 8, col2 = 14
  lines.push(`${'Qty'.padEnd(col1)}${'Unit'.padEnd(col2)}${L.ingredient.padEnd(28)}${L.brand}`)
  for (const ing of recipe.ingredients) {
    const brand = getBrand(ing.name, ing.ingredient_id) ?? ''
    const prep = ing.prep ? `  [${ing.prep}]` : ''
    lines.push(`${ing.qty.padEnd(col1)}${ing.unit.padEnd(col2)}${(ing.name + prep).padEnd(28)}${brand}`)
  }
  lines.push('')

  if (recipe.steps.length > 0) {
    lines.push(L.procedure)
    lines.push('-'.repeat(L.procedure.length))
    for (const s of recipe.steps) {
      lines.push(`${s.n}.  ${s.text}`)
    }
    lines.push('')
  }

  if (recipe.post_production?.length > 0) {
    lines.push(L.postProd)
    lines.push('-'.repeat(L.postProd.length))
    for (const p of recipe.post_production) {
      lines.push(`□  ${p.timing}: ${p.task}`)
    }
    lines.push('')
  }

  if (recipe.notes) {
    lines.push(L.notes)
    lines.push(recipe.notes)
  }

  return lines.join('\n').trimEnd()
}

function TextViewPanel({
  item,
  recipe,
  getBrand,
  onClose,
}: {
  item: MenuItem | null
  recipe: AiRecipe
  getBrand: (name: string, id?: string) => string | null
  onClose: () => void
}) {
  const [lang, setLang] = useState<PrintLang>('en')
  const [copied, setCopied] = useState(false)

  const text = generateRecipeText(item?.name ?? 'Recipe', recipe, getBrand, lang)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select the textarea
      const ta = document.getElementById('recipe-text-view') as HTMLTextAreaElement
      ta?.select()
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-modal z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
          <div>
            <h2 className="text-base font-semibold text-stone-900">Recipe Text</h2>
            <p className="text-xs text-stone-400 mt-0.5">Copy and share via WhatsApp, email, or any channel</p>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        {/* Language toggle */}
        <div className="flex gap-1 px-5 py-3 border-b border-stone-100">
          {(['en', 'es'] as const).map(l => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              className={[
                'px-4 py-1.5 rounded text-sm font-medium transition-colors',
                lang === l ? 'bg-brand-500 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200',
              ].join(' ')}
            >
              {l === 'en' ? 'English' : 'Español'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-hidden px-5 py-4 flex flex-col gap-3">
          <textarea
            id="recipe-text-view"
            readOnly
            value={text}
            className="flex-1 w-full rounded border border-stone-200 bg-stone-50 px-3 py-2.5 text-xs font-mono text-stone-800 resize-none focus:outline-none focus:ring-2 focus:ring-brand-500 leading-relaxed"
          />
        </div>

        <div className="px-5 py-4 border-t border-stone-200 flex gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className={[
              'flex-1 flex items-center justify-center gap-2 h-9 rounded text-sm font-medium transition-colors',
              copied
                ? 'bg-success text-white'
                : 'bg-brand-500 text-white hover:bg-brand-600',
            ].join(' ')}
          >
            {copied ? <Check size={14} /> : <FileText size={14} />}
            {copied ? 'Copied!' : 'Copy Text'}
          </button>
        </div>
      </div>
    </>
  )
}

function generatePrintHtml(
  itemName: string,
  recipe: AiRecipe,
  getBrand: (name: string, id?: string) => string | null,
  lang: PrintLang
): string {
  const L = PRINT_LABELS[lang]

  function ht(text: string): string {
    return text.replace(
      /(\d+(?:[–\-]\d+)?\s*(?:min(?:utes?)?|hours?|hrs?)|overnight)/gi,
      '<span class="timing">$1</span>'
    )
  }

  const prepHtml = recipe.prep.length > 0
    ? `<h2>${L.prep}</h2>${recipe.prep.map(p =>
        `<div class="block"><strong>${p.timing}</strong>${p.task}</div>`
      ).join('')}`
    : ''

  const ingRows = recipe.ingredients.map(ing => {
    const brand = getBrand(ing.name, ing.ingredient_id) ?? '—'
    const note = ing.prep ?? ''
    return `<tr>
      <td class="qty">${ing.qty} ${ing.unit}</td>
      <td>${ing.name}</td>
      <td>${brand}</td>
      <td class="note">${note}</td>
    </tr>`
  }).join('')

  const stepsHtml = recipe.steps.map(s => `<li>${ht(s.text)}</li>`).join('')

  const postHtml = (recipe.post_production?.length ?? 0) > 0
    ? `<h2>${L.postProd}</h2>${recipe.post_production.map(p =>
        `<div class="block"><strong>${p.timing}</strong>${p.task}</div>`
      ).join('')}`
    : ''

  const notesHtml = recipe.notes
    ? `<h2>${L.notes}</h2><div class="warning">${recipe.notes}</div>`
    : ''

  const metaParts: string[] = []
  if (recipe.serves) metaParts.push(`${L.yield}: <span class="yield">${recipe.serves}</span>`)
  if (recipe.total_time_minutes) metaParts.push(`${L.time}: <strong>${formatTime(recipe.total_time_minutes)}</strong>`)

  return `<!DOCTYPE html>
<html lang="${lang}">
<head>
<meta charset="UTF-8">
<title>${itemName}</title>
<style>
  body{font-family:sans-serif;max-width:680px;margin:40px auto;padding:0 20px;color:#222;line-height:1.6}
  h1{font-size:24px;margin-bottom:4px;font-weight:700}
  .sub{color:#888;font-size:13px;margin:0 0 4px}
  .meta{display:flex;gap:24px;font-size:14px;color:#555;margin:8px 0}
  h2{font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#888;margin:32px 0 10px;border-bottom:1px solid #eee;padding-bottom:6px;font-weight:600}
  table{width:100%;border-collapse:collapse;margin-bottom:8px}
  th{text-align:left;font-size:12px;color:#888;padding:6px 8px;background:#f5f5f5;font-weight:600}
  td{padding:8px;border-bottom:1px solid #f0f0f0;font-size:14px;vertical-align:top}
  .qty{font-weight:700;white-space:nowrap}
  .note{font-size:12px;color:#666;font-style:italic}
  ol{padding-left:20px;margin:0}
  ol li{padding:6px 0;font-size:14px}
  .timing{display:inline-block;background:#fef3c7;color:#92400e;font-weight:600;font-size:12px;padding:1px 7px;border-radius:4px;margin:0 2px}
  .block{background:#f9f9f9;border-left:3px solid #ccc;padding:10px 14px;margin-bottom:8px;font-size:14px}
  .block strong{display:block;font-size:12px;color:#888;margin-bottom:2px;text-transform:uppercase;font-weight:700}
  .warning{background:#fff8f0;border-left:3px solid #f97316;padding:10px 14px;font-size:13px;margin-bottom:8px}
  .yield{font-size:18px;font-weight:700;color:#166534}
</style>
</head>
<body>
<h1>${itemName}</h1>
<p class="sub">Kitchen SOP${lang === 'es' ? ' — Español' : ''}</p>
<div class="meta">${metaParts.join(' &nbsp;|&nbsp; ')}</div>
${prepHtml}
<h2>${L.ingredients}</h2>
<table>
  <thead><tr><th>${L.qty}</th><th>${L.ingredient}</th><th>${L.brand}</th><th>${L.prepNote}</th></tr></thead>
  <tbody>${ingRows}</tbody>
</table>
<h2>${L.procedure}</h2>
<ol>${stepsHtml}</ol>
${postHtml}
${notesHtml}
</body>
</html>`
}

function PrintPreviewModal({
  item,
  recipe,
  getBrand,
  lang,
  onClose,
}: {
  item: MenuItem | null
  recipe: AiRecipe
  getBrand: (name: string, id?: string) => string | null
  lang: PrintLang
  onClose: () => void
}) {
  function handlePrint() {
    const html = generatePrintHtml(item?.name ?? 'Recipe', recipe, getBrand, lang)
    const win = window.open('', '_blank', 'width=820,height=700')
    if (!win) return
    win.document.write(html)
    win.document.close()
    win.focus()
    setTimeout(() => win.print(), 150)
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-4 md:inset-10 bg-white rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-3 border-b border-stone-200 bg-white shrink-0">
          <h2 className="text-sm font-semibold text-stone-900">
            Print Preview — {lang === 'en' ? 'English' : 'Español'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-900 text-white text-xs font-semibold rounded hover:bg-stone-700 transition-colors"
            >
              <Printer size={13} />
              Print / Save PDF
            </button>
            <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600 ml-1">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-stone-100 p-6">
          <div className="max-w-2xl mx-auto bg-white shadow rounded-sm px-10 py-8">
            <RecipePrintView item={item} recipe={recipe} getBrand={getBrand} lang={lang} />
          </div>
        </div>
      </div>
    </>
  )
}

function RecipeSopTab({
  menuItemId,
  item,
}: {
  menuItemId: string
  item: MenuItem | null
}) {
  const restaurant = useAuthStore(s => s.restaurant)
  const restaurantId = restaurant?.id ?? ''
  const saveMenuItem = useSaveMenuItem()
  const { data: libraryIngredients } = useIngredients()
  const [previewLang, setPreviewLang] = useState<PrintLang | null>(null)
  const [quickAddName, setQuickAddName] = useState<string | null>(null)
  const [textViewOpen, setTextViewOpen] = useState(false)

  const libByName = useMemo(() => {
    const map = new Map<string, LibIngredient>()
    for (const lib of (libraryIngredients ?? []) as LibIngredient[]) {
      map.set(lib.name.toLowerCase().trim(), lib)
    }
    return map
  }, [libraryIngredients])

  const libById = useMemo(() => {
    const map = new Map<string, LibIngredient>()
    for (const lib of (libraryIngredients ?? []) as LibIngredient[]) {
      map.set(lib.id, lib)
    }
    return map
  }, [libraryIngredients])

  function matchLib(name: string, ingredientId?: string): LibIngredient | null {
    // Direct ID link is the most reliable — set when user picks from combobox
    if (ingredientId) {
      const byId = libById.get(ingredientId)
      if (byId) return byId
    }
    const key = name.toLowerCase().trim()
    if (libByName.has(key)) return libByName.get(key)!
    for (const [k, v] of libByName) {
      if (k.includes(key) || key.includes(k)) return v
    }
    return null
  }

  const [recipe, setRecipe] = useState<AiRecipe | null>(item?.ai_recipe ?? null)
  const [rawText, setRawText] = useState(item?.ai_recipe?.raw_notes ?? '')
  const [pasteOpen, setPasteOpen] = useState(!item?.ai_recipe)
  const [extracting, setExtracting] = useState(false)
  const [extractError, setExtractError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)

  // Inline edit state
  const [editing, setEditing] = useState<'ingredients' | 'prep' | 'steps' | 'postprod' | 'notes' | null>(null)

  const measurementSystem = restaurant?.measurement_system ?? 'metric'

  async function handleExtract() {
    if (!rawText.trim()) return
    setExtracting(true)
    setExtractError(null)
    try {
      const { data, error } = await supabase.functions.invoke('parse-recipe', {
        body: { raw_text: rawText, measurement_system: measurementSystem },
      })
      if (error) throw new Error(error.message)
      const parsed = data as AiRecipe
      setRecipe({ ...parsed, raw_notes: rawText })
      setPasteOpen(false)
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : 'Extraction failed. Check that ANTHROPIC_API_KEY is set in Supabase Edge Function secrets.')
    } finally {
      setExtracting(false)
    }
  }

  async function handleSave() {
    if (!recipe || !menuItemId) return
    setSaving(true)
    setSaveOk(false)
    try {
      await saveMenuItem.mutateAsync({ id: menuItemId, ai_recipe: recipe })
      setSaveOk(true)
    } finally {
      setSaving(false)
    }
  }

  function updateIngredient(i: number, field: keyof AiIngredient, value: string) {
    setRecipe(r => r ? {
      ...r,
      ingredients: r.ingredients.map((ing, idx) => idx === i ? { ...ing, [field]: value } : ing),
    } : r)
  }

  function addIngredient() {
    setRecipe(r => r ? { ...r, ingredients: [...r.ingredients, { qty: '', unit: '', name: '', prep: null }] } : r)
  }

  function removeIngredient(i: number) {
    setRecipe(r => r ? { ...r, ingredients: r.ingredients.filter((_, idx) => idx !== i) } : r)
  }

  function updatePrepStep(i: number, field: keyof AiPrepStep, value: string) {
    setRecipe(r => r ? {
      ...r,
      prep: r.prep.map((p, idx) => idx === i ? { ...p, [field]: value } : p),
    } : r)
  }

  function addPrepStep() {
    setRecipe(r => r ? { ...r, prep: [...r.prep, { timing: '', task: '' }] } : r)
  }

  function removePrepStep(i: number) {
    setRecipe(r => r ? { ...r, prep: r.prep.filter((_, idx) => idx !== i) } : r)
  }

  function updateStep(i: number, value: string) {
    setRecipe(r => r ? {
      ...r,
      steps: r.steps.map((s, idx) => idx === i ? { ...s, text: value } : s),
    } : r)
  }

  function moveStep(i: number, dir: 'up' | 'down') {
    setRecipe(r => {
      if (!r) return r
      const next = [...r.steps]
      const target = dir === 'up' ? i - 1 : i + 1
      if (target < 0 || target >= next.length) return r
      ;[next[i], next[target]] = [next[target], next[i]]
      return { ...r, steps: next.map((s, idx) => ({ ...s, n: idx + 1 })) }
    })
  }

  function addStep() {
    setRecipe(r => r ? {
      ...r,
      steps: [...r.steps, { n: r.steps.length + 1, text: '' }],
    } : r)
  }

  function removeStep(i: number) {
    setRecipe(r => r ? {
      ...r,
      steps: r.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, n: idx + 1 })),
    } : r)
  }

  function updatePostProd(i: number, field: keyof AiPostProdStep, value: string) {
    setRecipe(r => r ? {
      ...r,
      post_production: (r.post_production ?? []).map((p, idx) => idx === i ? { ...p, [field]: value } : p),
    } : r)
  }

  function addPostProd() {
    setRecipe(r => r ? { ...r, post_production: [...(r.post_production ?? []), { timing: '', task: '' }] } : r)
  }

  function removePostProd(i: number) {
    setRecipe(r => r ? { ...r, post_production: (r.post_production ?? []).filter((_, idx) => idx !== i) } : r)
  }

  if (!menuItemId) {
    return <p className="text-sm text-stone-400">Save the item details first to build a recipe.</p>
  }

  return (
    <>
      {previewLang && recipe && (
        <PrintPreviewModal
          item={item}
          recipe={recipe}
          getBrand={(name, id) => matchLib(name, id)?.brand ?? null}
          lang={previewLang}
          onClose={() => setPreviewLang(null)}
        />
      )}

      <div className="space-y-6 max-w-3xl">

        {/* Paste + Extract section */}
        <div className="card overflow-hidden">
          <button
            type="button"
            onClick={() => setPasteOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <RezzAiIcon size={16} />
              <span className="text-sm font-semibold text-stone-800">
                {recipe ? 'Re-extract from notes with RezzAI' : 'Ask RezzAI — paste recipe notes to extract'}
              </span>
            </div>
            {pasteOpen ? <ChevronUp size={15} className="text-stone-400" /> : <ChevronDown size={15} className="text-stone-400" />}
          </button>

          {pasteOpen && (
            <div className="border-t border-stone-100 p-4 space-y-3">
              <p className="text-xs text-stone-400">
                Paste your full recipe — ingredients, prep notes, cooking steps, everything. RezzAI will structure it automatically.
                Measurement system: <strong>{measurementSystem}</strong>
                {' '}(change in <a href="/settings" className="text-brand-500 hover:underline">Settings</a>).
              </p>
              <textarea
                rows={8}
                value={rawText}
                onChange={e => setRawText(e.target.value)}
                placeholder="Paste your recipe notes here — ingredients list, soaking times, grinding steps, cooking procedure, serving tips..."
                className="flex w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y"
              />
              {extractError && <p className="text-xs text-danger">{extractError}</p>}
              <Button
                onClick={handleExtract}
                loading={extracting}
                disabled={!rawText.trim()}
                size="sm"
              >
                <RezzAiIcon size={13} mono />
                {extracting ? 'Extracting...' : 'Extract with RezzAI'}
              </Button>
            </div>
          )}
        </div>

        {recipe && (
          <>
            {/* Header bar */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Yield</span>
                  <input
                    className="text-sm border-b border-dashed border-stone-300 bg-transparent text-stone-800 focus:outline-none focus:border-brand-400 w-40"
                    value={recipe.serves ?? ''}
                    onChange={e => setRecipe(r => r ? { ...r, serves: e.target.value } : r)}
                    placeholder="e.g. 4 portions of 200g"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock size={13} className="text-stone-400" />
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Time</span>
                  <input
                    type="number"
                    min="0"
                    className="text-sm border-b border-dashed border-stone-300 bg-transparent text-stone-800 focus:outline-none focus:border-brand-400 w-16 text-center"
                    value={recipe.total_time_minutes ?? ''}
                    onChange={e => setRecipe(r => r ? { ...r, total_time_minutes: e.target.value ? parseInt(e.target.value) : null } : r)}
                    placeholder="min"
                  />
                  <span className="text-xs text-stone-400">
                    {recipe.total_time_minutes ? formatTime(recipe.total_time_minutes) : 'minutes'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <UnitConverterWidget />
                <Button variant="outline" size="sm" onClick={() => setTextViewOpen(true)}>
                  <FileText size={13} />
                  Text
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPreviewLang('en')}>
                  <Printer size={13} />
                  EN
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPreviewLang('es')}>
                  <Printer size={13} />
                  ES
                </Button>
              </div>
            </div>

            {/* Ingredients */}
            <div className="card overflow-hidden">
              <div
                className="flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b border-stone-200 cursor-pointer"
                onClick={() => setEditing(e => e === 'ingredients' ? null : 'ingredients')}
              >
                <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Ingredients</span>
                <div className="flex items-center gap-3">
                  {editing !== 'ingredients' && recipe.ingredients.some(ing => !matchLib(ing.name, ing.ingredient_id)) && (
                    <span className="flex items-center gap-1 text-xs text-warning-dark">
                      <AlertCircle size={11} />
                      {recipe.ingredients.filter(ing => !matchLib(ing.name, ing.ingredient_id)).length} not in library
                    </span>
                  )}
                  <span className="text-xs text-stone-400">{editing === 'ingredients' ? 'done' : 'edit'}</span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 text-left">
                    <th className="px-4 py-2 text-xs font-medium text-stone-400 w-14">Qty</th>
                    <th className="px-2 py-2 text-xs font-medium text-stone-400 w-20">Unit</th>
                    <th className="px-2 py-2 text-xs font-medium text-stone-400">Ingredient</th>
                    <th className="px-2 py-2 text-xs font-medium text-stone-400">Brand</th>
                    <th className="px-2 py-2 text-xs font-medium text-stone-400">Prep note</th>
                    {editing === 'ingredients' && <th className="px-2 py-2 w-8" />}
                  </tr>
                </thead>
                <tbody>
                  {recipe.ingredients.map((ing, i) => {
                    const lib = matchLib(ing.name, ing.ingredient_id)
                    const notInLib = ing.name.trim().length > 0 && !lib
                    const unitMismatch = lib && lib.unit !== ing.unit
                    return (
                      <tr key={i} className="border-b border-stone-50 last:border-0">
                        <td className="px-4 py-2">
                          {editing === 'ingredients'
                            ? <input value={ing.qty} onChange={e => updateIngredient(i, 'qty', e.target.value)} className="w-14 h-7 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                            : <span className="text-stone-700 font-semibold">{ing.qty}</span>}
                        </td>
                        <td className="px-2 py-2">
                          {editing === 'ingredients'
                            ? <input value={ing.unit} onChange={e => updateIngredient(i, 'unit', e.target.value)} className="w-16 h-7 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
                            : (
                              <div className="flex items-center gap-1">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 text-xs font-medium">{ing.unit}</span>
                                {unitMismatch && (
                                  <span title={`Library uses ${lib!.unit} — auto-converted in costing`} className="text-warning-dark cursor-help"><AlertCircle size={11} /></span>
                                )}
                              </div>
                            )}
                        </td>
                        <td className="px-2 py-2">
                          {editing === 'ingredients' ? (
                            <div className="space-y-1">
                              <IngredientCombobox
                                value={ing.name}
                                library={(libraryIngredients ?? []) as LibIngredient[]}
                                onChange={(name, libEntry) => {
                                  setRecipe(r => {
                                    if (!r) return r
                                    return {
                                      ...r,
                                      ingredients: r.ingredients.map((row, idx) => idx !== i ? row : {
                                        ...row,
                                        name,
                                        ingredient_id: libEntry?.id ?? undefined,
                                        unit: (libEntry && !row.unit) ? libEntry.unit : row.unit,
                                      }),
                                    }
                                  })
                                }}
                              />
                              {notInLib && ing.name.trim() && (
                                <button
                                  type="button"
                                  onClick={() => setQuickAddName(ing.name)}
                                  className="text-xs text-brand-500 hover:text-brand-700 font-medium"
                                >
                                  + Add to library
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <span className="font-semibold text-stone-900">{ing.name}</span>
                              {notInLib && (
                                <span className="flex items-center gap-1 text-xs text-warning-dark">
                                  <AlertCircle size={10} /> Not in library
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {lib?.brand
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700 border border-brand-200 whitespace-nowrap">{lib.brand}</span>
                            : <span className="text-stone-300 text-xs">—</span>}
                        </td>
                        <td className="px-2 py-2">
                          {editing === 'ingredients'
                            ? <input value={ing.prep ?? ''} onChange={e => updateIngredient(i, 'prep', e.target.value || null as unknown as string)} className="w-full h-7 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" placeholder="prep note" />
                            : <span className="text-stone-400 text-xs italic">{ing.prep ?? ''}</span>}
                        </td>
                        {editing === 'ingredients' && (
                          <td className="px-2 py-2">
                            <button type="button" onClick={() => removeIngredient(i)} className="text-stone-300 hover:text-danger text-xs">✕</button>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {editing === 'ingredients' && (
                <div className="px-4 py-2 border-t border-stone-100 flex items-center justify-between">
                  <button type="button" onClick={addIngredient} className="text-xs text-brand-500 hover:text-brand-700 font-medium">+ Add ingredient</button>
                  <Link to="/inventory" className="text-xs text-stone-400 hover:text-stone-600">Manage library →</Link>
                </div>
              )}
            </div>
            {/* Quick-add ingredient to library */}
            {quickAddName !== null && (
              <QuickAddIngredientPanel
                initialName={quickAddName}
                restaurantId={restaurantId}
                onClose={() => setQuickAddName(null)}
                onAdded={() => setQuickAddName(null)}
              />
            )}

            {/* Prep (Do Ahead) */}
            {(recipe.prep.length > 0 || editing === 'prep') && (
              <div className="card overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-2.5 bg-amber-50 border-b border-amber-100 cursor-pointer"
                  onClick={() => setEditing(e => e === 'prep' ? null : 'prep')}
                >
                  <div className="flex items-center gap-2">
                    <Clock size={13} className="text-amber-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Prep — Do Ahead</span>
                  </div>
                  <span className="text-xs text-amber-500">{editing === 'prep' ? 'done' : 'edit'}</span>
                </div>
                <div className="divide-y divide-stone-50">
                  {recipe.prep.map((p, i) => (
                    <div key={i} className="px-4 py-3 flex gap-3 items-start">
                      {editing === 'prep' ? (
                        <>
                          <input
                            value={p.timing}
                            onChange={e => updatePrepStep(i, 'timing', e.target.value)}
                            placeholder="When"
                            className="h-7 w-36 rounded border border-stone-300 px-1.5 text-sm font-semibold text-stone-600 focus:outline-none focus:ring-1 focus:ring-brand-500 flex-shrink-0"
                          />
                          <input
                            value={p.task}
                            onChange={e => updatePrepStep(i, 'task', e.target.value)}
                            placeholder="What to do"
                            className="h-7 flex-1 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                          />
                          <button type="button" onClick={() => removePrepStep(i)} className="text-stone-300 hover:text-danger text-xs flex-shrink-0">✕</button>
                        </>
                      ) : (
                        <>
                          <span className="text-sm font-semibold text-stone-600 whitespace-nowrap">{p.timing}:</span>
                          <span className="text-sm text-stone-700">{p.task}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {editing === 'prep' && (
                  <div className="px-4 py-2 border-t border-stone-100">
                    <button type="button" onClick={addPrepStep} className="text-xs text-brand-500 hover:text-brand-700 font-medium">+ Add prep step</button>
                  </div>
                )}
              </div>
            )}

            {/* Add prep button if none and not editing */}
            {recipe.prep.length === 0 && editing !== 'prep' && (
              <button
                type="button"
                onClick={() => { addPrepStep(); setEditing('prep') }}
                className="text-xs text-stone-400 hover:text-amber-600 flex items-center gap-1.5 transition-colors"
              >
                <Clock size={12} />
                Add prep steps (soaking, grinding, marinating...)
              </button>
            )}

            {/* Procedure */}
            <div className="card overflow-hidden">
              <div
                className="flex items-center justify-between px-4 py-2.5 bg-stone-50 border-b border-stone-200 cursor-pointer"
                onClick={() => setEditing(e => e === 'steps' ? null : 'steps')}
              >
                <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Procedure</span>
                <span className="text-xs text-stone-400">{editing === 'steps' ? 'done' : 'edit'}</span>
              </div>
              <ol className="divide-y divide-stone-50">
                {recipe.steps.map((step, i) => (
                  <li key={step.n} className="flex items-start gap-3 px-4 py-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-800 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                      {step.n}
                    </span>
                    {editing === 'steps' ? (
                      <>
                        <textarea
                          rows={2}
                          value={step.text}
                          onChange={e => updateStep(i, e.target.value)}
                          className="flex-1 rounded border border-stone-300 px-2 py-1.5 text-sm text-stone-800 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-none"
                        />
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button type="button" onClick={() => moveStep(i, 'up')} disabled={i === 0} className="text-stone-300 hover:text-stone-600 disabled:opacity-20">
                            <ChevronUp size={13} />
                          </button>
                          <button type="button" onClick={() => moveStep(i, 'down')} disabled={i === recipe.steps.length - 1} className="text-stone-300 hover:text-stone-600 disabled:opacity-20">
                            <ChevronDown size={13} />
                          </button>
                          <button type="button" onClick={() => removeStep(i)} className="text-stone-300 hover:text-danger">
                            <span className="text-xs">✕</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-stone-800 leading-snug">{step.text}</p>
                    )}
                  </li>
                ))}
              </ol>
              {editing === 'steps' && (
                <div className="px-4 py-2 border-t border-stone-100">
                  <button type="button" onClick={addStep} className="text-xs text-brand-500 hover:text-brand-700 font-medium">+ Add step</button>
                </div>
              )}
            </div>

            {/* Post-production */}
            {((recipe.post_production ?? []).length > 0 || editing === 'postprod') && (
              <div className="card overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-2.5 bg-indigo-50 border-b border-indigo-100 cursor-pointer"
                  onClick={() => setEditing(e => e === 'postprod' ? null : 'postprod')}
                >
                  <div className="flex items-center gap-2">
                    <PackageCheck size={13} className="text-indigo-600" />
                    <span className="text-xs font-bold uppercase tracking-widest text-indigo-700">Post-Production &amp; Packing</span>
                  </div>
                  <span className="text-xs text-indigo-400">{editing === 'postprod' ? 'done' : 'edit'}</span>
                </div>
                <div className="divide-y divide-stone-50">
                  {(recipe.post_production ?? []).map((p, i) => (
                    <div key={i} className="px-4 py-3 flex gap-3 items-start">
                      {editing === 'postprod' ? (
                        <>
                          <input
                            value={p.timing}
                            onChange={e => updatePostProd(i, 'timing', e.target.value)}
                            placeholder="When"
                            className="h-7 w-40 rounded border border-stone-300 px-1.5 text-sm font-semibold text-stone-600 focus:outline-none focus:ring-1 focus:ring-brand-500 flex-shrink-0"
                          />
                          <input
                            value={p.task}
                            onChange={e => updatePostProd(i, 'task', e.target.value)}
                            placeholder="What to do"
                            className="h-7 flex-1 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                          />
                          <button type="button" onClick={() => removePostProd(i)} className="text-stone-300 hover:text-danger text-xs flex-shrink-0">✕</button>
                        </>
                      ) : (
                        <>
                          <span className="text-sm font-semibold text-stone-600 whitespace-nowrap">{p.timing}:</span>
                          <span className="text-sm text-stone-700">{p.task}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {editing === 'postprod' && (
                  <div className="px-4 py-2 border-t border-stone-100">
                    <button type="button" onClick={addPostProd} className="text-xs text-brand-500 hover:text-brand-700 font-medium">+ Add step</button>
                  </div>
                )}
              </div>
            )}

            {/* Add post-prod button if none */}
            {(recipe.post_production ?? []).length === 0 && editing !== 'postprod' && (
              <button
                type="button"
                onClick={() => { addPostProd(); setEditing('postprod') }}
                className="text-xs text-stone-400 hover:text-indigo-600 flex items-center gap-1.5 transition-colors"
              >
                <PackageCheck size={12} />
                Add post-production steps (cool-down, packing, labeling, storage...)
              </button>
            )}

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-widest text-stone-400">Chef&apos;s Notes</label>
              <textarea
                rows={2}
                value={recipe.notes ?? ''}
                onChange={e => setRecipe(r => r ? { ...r, notes: e.target.value || null } : r)}
                placeholder="Serving suggestions, tips, variations..."
                className="flex w-full rounded border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
              />
            </div>

            {/* Save */}
            <div className="flex items-center gap-3">
              <Button onClick={handleSave} loading={saving}>
                Save Recipe & SOP
              </Button>
              {saveOk && <span className="text-sm text-success">Saved!</span>}
            </div>
          </>
        )}
      </div>

      {/* Text view panel */}
      {textViewOpen && recipe && (
        <TextViewPanel
          item={item}
          recipe={recipe}
          getBrand={(name, id) => matchLib(name, id)?.brand ?? null}
          onClose={() => setTextViewOpen(false)}
        />
      )}
    </>
  )
}

function fmt(n: number) {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function CostingTab({ item }: { item: MenuItem | null }) {
  const saveMenuItem = useSaveMenuItem()
  const { data: libraryIngredients } = useIngredients()

  const defaultCosting: MenuItemCosting = {
    batch_size: 1,
    labor_hours_per_batch: 0,
    labor_cost_per_hour: 0,
    packaging_cost_per_serving: 0,
    delivery_cost_per_serving: 0,
    ingredient_costs: {},
  }

  const [costing, setCosting] = useState<MenuItemCosting>(
    item?.costing ?? defaultCosting
  )
  const [saving, setSaving] = useState(false)
  const [saveOk, setSaveOk] = useState(false)
  // Local string values for cost inputs to avoid decimal-typing issues with type="number"
  const [costInputs, setCostInputs] = useState<Record<string, string>>({})
  const [packagingInput, setPackagingInput] = useState(String(item?.costing?.packaging_cost_per_serving ?? ''))
  const [deliveryInput, setDeliveryInput] = useState(String(item?.costing?.delivery_cost_per_serving ?? ''))

  // Build ingredient rows from AI recipe
  const aiIngredients = item?.ai_recipe?.ingredients ?? []

  // Auto-match AI ingredient → { cost per library unit, library unit }
  // Priority: 1. direct ID link (set when user picks from combobox)  2. name fuzzy match
  const autoMatch = useMemo(() => {
    const map: Record<string, { cost: number | null; libUnit: string | null; libName: string | null }> = {}
    const libs = (libraryIngredients ?? [])
    for (const ai of aiIngredients) {
      // 1. Direct ID link
      let found = ai.ingredient_id ? libs.find(lib => lib.id === ai.ingredient_id) : undefined
      // 2. Name fallback
      if (!found) {
        const key = ai.name.toLowerCase().trim()
        found = libs.find(lib => {
          const lk = lib.name.toLowerCase().trim()
          return lk === key || lk.includes(key) || key.includes(lk)
        })
      }
      map[ai.name] = {
        cost: found?.cost_per_unit ?? null,
        libUnit: found?.unit ?? null,
        libName: found?.name ?? null,
      }
    }
    return map
  }, [aiIngredients, libraryIngredients])

  // Cost per recipe unit (with unit conversion from library unit)
  function getCostPerRecipeUnit(name: string, recipeUnit: string): number {
    // Manual override takes precedence
    if (costing.ingredient_costs[name] !== undefined) return costing.ingredient_costs[name]
    const m = autoMatch[name]
    if (m?.cost != null && m.libUnit) {
      const factor = convertQty(1, recipeUnit, m.libUnit)
      if (factor !== null) return m.cost * factor
    }
    return 0
  }

  function setIngredientCost(name: string, value: number | null) {
    if (value === null) {
      setCosting(c => {
        const next = { ...c.ingredient_costs }
        delete next[name]
        return { ...c, ingredient_costs: next }
      })
    } else {
      setCosting(c => ({ ...c, ingredient_costs: { ...c.ingredient_costs, [name]: value } }))
    }
  }

  function clearIngredientCost(name: string) {
    setIngredientCost(name, null)
    setCostInputs(p => { const n = { ...p }; delete n[name]; return n })
  }

  // Calculations
  const batchSize = Math.max(1, costing.batch_size)

  const ingredientCostPerBatch = aiIngredients.reduce((sum, ing) => {
    const qty = parseFloat(ing.qty) || 0
    return sum + qty * getCostPerRecipeUnit(ing.name, ing.unit ?? '')
  }, 0)
  const ingredientCostPerServing = ingredientCostPerBatch / batchSize

  const laborCostPerBatch = costing.labor_hours_per_batch * costing.labor_cost_per_hour
  const laborCostPerServing = laborCostPerBatch / batchSize

  const packagingPerServing = costing.packaging_cost_per_serving
  const deliveryPerServing = costing.delivery_cost_per_serving

  const totalCostPerServing = ingredientCostPerServing + laborCostPerServing + packagingPerServing + deliveryPerServing
  const salePrice = item?.price ?? 0
  const grossProfit = salePrice - totalCostPerServing
  const grossMargin = salePrice > 0 ? (grossProfit / salePrice) * 100 : 0
  const isLoss = grossProfit < 0

  async function handleSave() {
    if (!item?.id) return
    setSaving(true)
    setSaveOk(false)
    try {
      await saveMenuItem.mutateAsync({ id: item.id, costing })
      setSaveOk(true)
    } finally {
      setSaving(false)
    }
  }

  if (!item?.id) {
    return <p className="text-sm text-stone-400">Save the item details first.</p>
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Batch size */}
      <div className="card p-4 flex items-center gap-6 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-stone-700">This recipe makes</span>
          <input
            type="number"
            min="1"
            value={costing.batch_size}
            onChange={e => setCosting(c => ({ ...c, batch_size: parseInt(e.target.value) || 1 }))}
            className="w-16 h-8 rounded border border-stone-300 px-2 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <span className="text-sm font-medium text-stone-700">servings / portions</span>
        </div>
        {item.ai_recipe?.serves && (
          <span className="text-xs text-stone-400 italic">Recipe says: {item.ai_recipe.serves}</span>
        )}
      </div>

      {/* Ingredients */}
      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Ingredient Costs</span>
          <span className="text-xs text-stone-400">Cost is converted to the recipe unit automatically</span>
        </div>
        {aiIngredients.length === 0 ? (
          <p className="px-4 py-6 text-sm text-stone-400 text-center">
            Add a recipe in the Recipe &amp; SOP tab first — ingredients will appear here for costing.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100 text-left">
                <th className="px-4 py-2 text-xs font-medium text-stone-400">Ingredient</th>
                <th className="px-3 py-2 text-xs font-medium text-stone-400 w-14">Qty</th>
                <th className="px-3 py-2 text-xs font-medium text-stone-400 w-16">Unit</th>
                <th className="px-3 py-2 text-xs font-medium text-stone-400 w-36">$ / recipe unit</th>
                <th className="px-3 py-2 text-xs font-medium text-stone-400 text-right w-24">Line cost</th>
              </tr>
            </thead>
            <tbody>
              {aiIngredients.map((ing, i) => {
                const recipeUnit = ing.unit ?? ''
                const costPerUnit = getCostPerRecipeUnit(ing.name, recipeUnit)
                const qty = parseFloat(ing.qty) || 0
                const lineCost = qty * costPerUnit
                const m = autoMatch[ing.name]
                const isAutoMatched = m?.cost != null && costing.ingredient_costs[ing.name] === undefined
                const hasManualOverride = costing.ingredient_costs[ing.name] !== undefined
                const canConvert = m?.libUnit ? convertQty(1, recipeUnit, m.libUnit) !== null : false
                const cantConvert = m?.cost != null && m.libUnit && !canConvert

                // Local input display value
                const inputDisplay = costInputs[ing.name] !== undefined
                  ? costInputs[ing.name]
                  : costPerUnit > 0 ? String(costPerUnit) : ''

                return (
                  <tr key={i} className="border-b border-stone-50 last:border-0">
                    <td className="px-4 py-2">
                      <div>
                        <span className="font-medium text-stone-900">{ing.name}</span>
                        {isAutoMatched && canConvert && (
                          <span className="ml-1.5 text-xs text-brand-500">✓ linked</span>
                        )}
                        {hasManualOverride && (
                          <button type="button" onClick={() => clearIngredientCost(ing.name)} className="ml-1.5 text-xs text-stone-400 hover:text-danger" title="Clear override, revert to library">✕ override</button>
                        )}
                        {!isAutoMatched && !hasManualOverride && (
                          <span className="ml-1.5 text-xs text-warning-dark">not in library — enter manually</span>
                        )}
                        {cantConvert && (
                          <span className="ml-1.5 text-xs text-warning-dark" title={`Library unit is ${m?.libUnit} — enter cost per ${recipeUnit} manually`}>unit mismatch</span>
                        )}
                      </div>
                      {isAutoMatched && m?.libName && m.libName.toLowerCase() !== ing.name.toLowerCase() && (
                        <div className="text-xs text-stone-400 mt-0.5">matched: {m.libName}</div>
                      )}
                      {isAutoMatched && m?.libUnit && recipeUnit && normUnit(recipeUnit) !== normUnit(m.libUnit) && canConvert && (
                        <div className="text-xs text-stone-400">
                          ${Number(m.cost).toFixed(4)}/{m.libUnit} → converted
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-stone-600 font-medium">{ing.qty}</td>
                    <td className="px-3 py-2 text-stone-400">{recipeUnit || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <span className="text-stone-400 text-xs">$</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={inputDisplay}
                          onChange={e => setCostInputs(p => ({ ...p, [ing.name]: e.target.value }))}
                          onBlur={e => {
                            const raw = e.target.value.trim()
                            const parsed = parseFloat(raw)
                            if (raw === '' || isNaN(parsed)) {
                              clearIngredientCost(ing.name)
                            } else {
                              setIngredientCost(ing.name, parsed)
                              setCostInputs(p => { const n = {...p}; delete n[ing.name]; return n })
                            }
                          }}
                          placeholder="0.0000"
                          className="w-24 h-7 rounded border border-stone-300 px-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-stone-700">
                      {lineCost > 0 ? `$${fmt(lineCost)}` : <span className="text-stone-300">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-stone-200 bg-stone-50">
                <td colSpan={4} className="px-4 py-2 text-xs font-semibold text-stone-500 text-right">
                  Total ingredients / batch &nbsp;|&nbsp; per serving
                </td>
                <td className="px-3 py-2 text-right font-semibold text-stone-800">
                  ${fmt(ingredientCostPerBatch)} &nbsp;|&nbsp; ${fmt(ingredientCostPerServing)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Labor */}
      <div className="card p-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500">Labor</p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-700">Effort</span>
            <input
              type="text"
              inputMode="decimal"
              value={costing.labor_hours_per_batch || ''}
              onChange={e => setCosting(c => ({ ...c, labor_hours_per_batch: parseFloat(e.target.value) || 0 }))}
              placeholder="0"
              className="w-16 h-8 rounded border border-stone-300 px-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <span className="text-sm text-stone-700">hrs per batch ×</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-400">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={costing.labor_cost_per_hour || ''}
              onChange={e => setCosting(c => ({ ...c, labor_cost_per_hour: parseFloat(e.target.value) || 0 }))}
              placeholder="0"
              className="w-20 h-8 rounded border border-stone-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <span className="text-sm text-stone-700">/ hr</span>
          </div>
          {laborCostPerBatch > 0 && (
            <span className="text-sm text-stone-500 ml-auto">
              = <span className="font-semibold text-stone-800">${fmt(laborCostPerBatch)}</span> / batch &nbsp;·&nbsp;
              <span className="font-semibold text-stone-800">${fmt(laborCostPerServing)}</span> / serving
            </span>
          )}
        </div>
      </div>

      {/* Other costs */}
      <div className="card p-4 space-y-3">
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500">Other Costs (per serving)</p>
        <div className="grid grid-cols-2 gap-4 max-w-sm">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-stone-500">Packaging</label>
            <Input
              type="text"
              inputMode="decimal"
              prefix="$"
              value={packagingInput}
              onChange={e => setPackagingInput(e.target.value)}
              onBlur={e => {
                const v = parseFloat(e.target.value) || 0
                setCosting(c => ({ ...c, packaging_cost_per_serving: v }))
                setPackagingInput(String(v || ''))
              }}
              placeholder="0.00"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-stone-500">Delivery / overhead</label>
            <Input
              type="text"
              inputMode="decimal"
              prefix="$"
              value={deliveryInput}
              onChange={e => setDeliveryInput(e.target.value)}
              onBlur={e => {
                const v = parseFloat(e.target.value) || 0
                setCosting(c => ({ ...c, delivery_cost_per_serving: v }))
                setDeliveryInput(String(v || ''))
              }}
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className={[
        'card p-5 space-y-3',
        isLoss ? 'border-danger' : '',
      ].join(' ')}>
        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 flex items-center gap-2">
          <TrendingUp size={13} />
          Cost vs. Price Summary
        </p>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between text-stone-600">
            <span>Ingredient cost</span>
            <span className="font-medium">${fmt(ingredientCostPerServing)}</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Labor</span>
            <span className="font-medium">${fmt(laborCostPerServing)}</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Packaging</span>
            <span className="font-medium">${fmt(packagingPerServing)}</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Delivery / overhead</span>
            <span className="font-medium">${fmt(deliveryPerServing)}</span>
          </div>
          <div className="h-px bg-stone-200 my-2" />
          <div className="flex justify-between font-semibold text-stone-900">
            <span>Total cost / serving</span>
            <span>${fmt(totalCostPerServing)}</span>
          </div>
          <div className="flex justify-between text-stone-500 text-xs">
            <span>Sale price <span className="text-stone-400">(set in Details)</span></span>
            <span className="font-semibold text-stone-700 text-sm">${fmt(salePrice)}</span>
          </div>
          <div className="h-px bg-stone-200 my-2" />
          <div className={['flex justify-between font-bold text-base', isLoss ? 'text-danger' : 'text-success'].join(' ')}>
            <span>Gross profit</span>
            <span>{isLoss ? '-' : '+'}${fmt(Math.abs(grossProfit))}</span>
          </div>
          <div className={['flex justify-between text-sm font-semibold', isLoss ? 'text-danger' : 'text-success'].join(' ')}>
            <span>Gross margin</span>
            <span>{fmt(grossMargin)}%</span>
          </div>
          {isLoss && (
            <p className="text-xs text-danger bg-danger-light rounded px-3 py-2 mt-2">
              Sale price is below cost. Raise the price or reduce costs to be profitable.
            </p>
          )}
          {!isLoss && grossMargin < 20 && (
            <p className="text-xs text-warning-dark bg-warning-light rounded px-3 py-2 mt-2">
              Margin is below 20% — consider reviewing your pricing or ingredient costs.
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} loading={saving}>
          Save Costing
        </Button>
        {saveOk && <span className="text-sm text-success">Saved!</span>}
      </div>
    </div>
  )
}

export function MenuItemDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isNew = !id

  const role = useAuthStore(s => s.role)
  const { data: item, isLoading } = useMenuItem(id ?? '')
  // Deep-link support — the Recipes list links straight into this tab.
  const [activeTab, setActiveTab] = useState<Tab>(searchParams.get('tab') === 'recipe' ? 'recipe' : 'details')
  const [savedItem, setSavedItem] = useState<MenuItem | null>(null)

  const displayItem = savedItem ?? item ?? null
  const menuItemId = savedItem?.id ?? id ?? ''

  if (!isNew && isLoading) return <PageSpinner />

  const tabs: { key: Tab; label: string; ownerOnly?: boolean }[] = [
    { key: 'details', label: 'Details' },
    { key: 'recipe', label: 'Recipe & SOP' },
    { key: 'costing', label: 'Costing & Pricing', ownerOnly: true },
  ]

  function handleSaved(saved: MenuItem) {
    setSavedItem(saved)
    if (isNew) {
      navigate(`/products/items/${saved.id}`, { replace: true })
    }
    setActiveTab('recipe')
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/products">
            <ArrowLeft size={14} />
            Products
          </Link>
        </Button>
      </div>

      <div className="page-header">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">
            {displayItem?.name ?? 'New Product'}
          </h1>
          {displayItem?.status && (
            <p className="text-sm text-stone-500 mt-0.5 capitalize">{displayItem.status}</p>
          )}
        </div>
      </div>

      <div className="border-b border-stone-200">
        <div className="flex gap-0">
          {tabs.map(tab => {
            if (tab.ownerOnly && role !== 'owner') return null
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={[
                  'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5',
                  activeTab === tab.key
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-stone-500 hover:text-stone-700',
                ].join(' ')}
              >
                {tab.ownerOnly && <Lock size={11} className="opacity-50" />}
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      <div>
        {activeTab === 'details' && (
          <DetailsTab item={displayItem} onSaved={handleSaved} />
        )}
        {activeTab === 'recipe' && (
          <RecipeSopTab menuItemId={menuItemId} item={displayItem} />
        )}
        {activeTab === 'costing' && role === 'owner' && (
          <CostingTab item={displayItem} />
        )}
      </div>
    </div>
  )
}
